import { setMultipleRefs } from '@elliemae/ds-system';
import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import React, { useCallback, useMemo } from 'react';
import type { ControlledDateTimePickerContextT } from '../../config/useControlledDateTimePicker.js';
import { ControlledDateTimePickerDatatestid } from '../../constants/index.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
import { StyledMonthInput } from '../Styleds.js';
import { convertToPositiveNumberIfPossible } from '../../utils/numberHelpers.js';
import { monthNames } from '../../utils/constants.js';

const regExpNumbers = /^[0-9]+$/;
const isInvalidMonth = (month: string) => {
  if (month === '') return false;
  const numberOnly = regExpNumbers.test(month);
  return month.length > 2 || month === '00' || !numberOnly || Number.parseInt(month, 10) > 12;
};
interface MMInputsPropsT {
  onChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  onBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  autoFocusMonthInput: boolean;
  value: string;
  disabled: boolean;
  applyAriaDisabled?: boolean;
  hasError: boolean;
  contextRef: React.MutableRefObject<HTMLInputElement | null>;
  placeholder: string;
  isFocused: boolean;
  innerRef?:
    | React.MutableRefObject<HTMLInputElement | HTMLButtonElement | null>
    | ((ref: HTMLInputElement | HTMLButtonElement | null) => void);
  tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  getProps: ControlledDateTimePickerContextT['getProps'];
  readOnly: boolean;
}
export const MMInput = React.memo(
  ({
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    value,
    disabled,
    applyAriaDisabled,
    hasError,
    placeholder,
    contextRef,
    innerRef,
    tabIndex,
    autoFocusMonthInput,
    isFocused,
    getProps,
    readOnly,
  }: MMInputsPropsT): JSX.Element => {
    const validateOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isInvalidMonth(e?.target?.value)) {
          e.preventDefault();
          return;
        }
        onChange(e);
      },
      [onChange],
    );

    const handleRef = useCallback(
      (DomElem: HTMLInputElement) => {
        (contextRef as React.MutableRefObject<HTMLInputElement>).current = DomElem;
        if (isFocused) DomElem?.focus?.();
      },
      [contextRef, isFocused],
    );

    const currentRef = useMemo(() => {
      if (innerRef) {
        return setMultipleRefs([handleRef, innerRef]);
      }
      return handleRef;
    }, [handleRef, innerRef]);

    const monthNum = convertToPositiveNumberIfPossible(value);

    return (
      <StyledMonthInput
        autoFocus={autoFocusMonthInput}
        onChange={validateOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        innerRef={currentRef}
        tabIndex={tabIndex}
        placeholder={placeholder}
        aria-label="month"
        data-testid={ControlledDateTimePickerDatatestid.DATE_INPUTS.MONTH}
        disabled={disabled}
        aria-disabled={applyAriaDisabled}
        aria-invalid={hasError}
        getOwnerProps={getProps}
        readOnly={readOnly}
        role="spinbutton"
        aria-valuemin={1}
        aria-valuemax={12}
        // when empty, valuenow falls back to the min (not omitted) so VoiceOver honors
        // aria-valuetext="empty" instead of announcing a computed position percentage
        aria-valuenow={monthNum === -1 ? 1 : monthNum}
        aria-valuetext={monthNum === -1 ? 'empty' : monthNames[monthNum - 1]}
      />
    );
  },
);
